// SEQ-180 legibility follow-up (15 September 2026): the axis domain a scatter
// on this page actually draws, as a pure module so it can be proven under Deno.
//
// THE DEFECT THIS FIXES, measured from rendered pixels on production data.
// "Profit vs overtime" drew a Y domain of -1500% to 500% because one project,
// Tapestry - Chevron Island, sits at -1360.1% profit. Twenty of twenty-two
// points then collapsed into a band a few pixels tall around zero, so a project
// at 48% and a project at 2.7% were visually the same project. "Profit vs
// patching" did the same thing sideways, 0% to 600%, off three projects.
//
// WHY CLAMPING AND NOT A LOG SCALE. A symmetric log axis would fit everything
// on screen, and it would do it by making equal distances mean unequal
// amounts. The reader this page was rebuilt for is the one who has never seen
// it before, and a log axis is a thing you have to be taught. A linear axis
// with a stated cut is a thing you can read: the ruler stays even, and the
// three projects that do not fit are named in words underneath rather than
// being turned into a curve. Profit % also runs through zero, which a log scale
// has no honest answer for.
//
// WHAT THE CLAMP MUST NEVER DO IS HIDE THE OUTLIER. A project at -1360% profit
// is the most important fact on the page. It keeps a mark on the chart, pinned
// to the edge it ran off and drawn as a different shape so it cannot be read as
// an ordinary point, and its real figure is printed under the chart.

/**
 * The share of the points that may be called outliers before the fence is
 * judged too tight to be describing outliers at all.
 *
 * A metric where most projects read zero (patching, on a good month) has an
 * interquartile range near zero, and a fence built from it would call every
 * non-zero project exceptional. Past this share the rule widens itself, and if
 * the wider rule still cuts too much, nothing is clamped: an axis that pins a
 * third of its data is not an axis with outliers, it is the wrong axis.
 */
export const OUTLIER_SHARE_MAX = 0.2;

/** Tukey's constant. 1.5 interquartile ranges past the quartiles is the textbook fence. */
export const FENCE_MULTIPLIER = 1.5;

/** The wider fallback fence, as percentiles, used when Tukey's cuts too much. */
export const WIDE_FENCE_PERCENTILES: readonly [number, number] = [0.02, 0.98];

/** Breathing room at each end of the drawn domain, as a share of the data's own span. */
export const DOMAIN_PAD_SHARE = 0.06;

/** The steps a rounded axis bound is allowed to land on, per order of magnitude. */
const NICE_STEPS: readonly number[] = [1, 2, 2.5, 5, 10];

export type AxisDomain = [number, number];

export interface ClampedAxis {
  /** The domain to hand Recharts. */
  domain: AxisDomain;
  /** True when at least one value fell outside and is being pinned to an edge. */
  clamped: boolean;
  /** How many values sit below the domain, and how many above. */
  below: number;
  above: number;
}

/** A finite number, or nothing. Rows on this page carry nulls for a project with no data. */
function finiteOnly(values: readonly (number | null | undefined)[]): number[] {
  return values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}

/**
 * The value at percentile `p` of an already-sorted list, interpolating between
 * neighbours rather than snapping to one, so a 20-point list does not quantise
 * its quartiles to whichever value happens to sit at index five.
 */
export function quantile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];
  const position = (sorted.length - 1) * Math.min(Math.max(p, 0), 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

/** The smallest "round" number at or above `raw`: 1, 2, 2.5, 5 or 10 times a power of ten. */
export function niceStep(raw: number): number {
  const magnitude = Math.abs(raw);
  if (!Number.isFinite(magnitude) || magnitude === 0) return 1;
  const power = Math.pow(10, Math.floor(Math.log10(magnitude)));
  for (const step of NICE_STEPS) {
    if (step * power >= magnitude - Number.EPSILON) return step * power;
  }
  return 10 * power;
}

/** A fence pair, or null when the data cannot support one. */
function tukeyFence(sorted: number[]): [number, number] | null {
  const q1 = quantile(sorted, 0.25);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;
  if (!(iqr > 0)) return null;
  return [q1 - FENCE_MULTIPLIER * iqr, q3 + FENCE_MULTIPLIER * iqr];
}

function outsideCount(values: readonly number[], fence: [number, number]): number {
  return values.filter((v) => v < fence[0] || v > fence[1]).length;
}

/**
 * The drawn domain for a set of values, with the tail cut off where the tail is
 * distorting the ruler.
 *
 * Zero is always inside the domain. Every axis this is used on is a percentage
 * of something, so break-even is the line the whole chart is read against, and
 * an axis that does not contain it is answering a different question.
 *
 * The padded end is only opened up where something is actually there: an axis
 * whose values are all positive, with nothing pinned below it, does not drop
 * below zero just to leave a margin under the lowest point.
 */
export function clampedAxisDomain(values: readonly (number | null | undefined)[]): ClampedAxis {
  const numbers = finiteOnly(values);
  if (numbers.length === 0) return { domain: [0, 1], clamped: false, below: 0, above: 0 };

  const sorted = [...numbers].sort((a, b) => a - b);
  const allowed = Math.floor(numbers.length * OUTLIER_SHARE_MAX);

  let fence = tukeyFence(sorted);
  if (fence !== null && outsideCount(sorted, fence) > allowed) {
    fence = [quantile(sorted, WIDE_FENCE_PERCENTILES[0]), quantile(sorted, WIDE_FENCE_PERCENTILES[1])];
  }
  if (fence !== null && outsideCount(sorted, fence) > allowed) fence = null;

  const inRange = fence === null ? sorted : sorted.filter((v) => v >= fence[0] && v <= fence[1]);
  const kept = inRange.length > 0 ? inRange : sorted;

  const low = Math.min(kept[0], 0);
  const high = Math.max(kept[kept.length - 1], 0);
  const cutBelow = numbers.some((v) => v < low);
  const cutAbove = numbers.some((v) => v > high);

  const span = high - low || Math.abs(high) || 1;
  const pad = span * DOMAIN_PAD_SHARE;
  const step = niceStep(span / 8);

  // A pinned mark needs somewhere to sit that is not on top of the lowest real
  // point, so the end it ran off gets the padding whether or not the data
  // reaches there. The other end only gets it if the data does.
  //
  // THE PADDING IS THE ONLY MECHANISM, and it is enough. This used to be
  // followed by a second rule that pushed the boundary out one more step
  // "because rounding can swallow the padding whole when the data already sits
  // on a round number". That cannot happen, and the second rule was therefore
  // dead code with a comment describing an impossibility. `Math.floor(x / step)
  // * step <= x` for every x, and here x is `low - pad`, which is already below
  // `low`, so the floor is always strictly below `low` as well. Probed at 2
  // million random low/high pairs across seven orders of magnitude and at every
  // boundary case the old comment named: the rule fired zero times. It was
  // removed on 15 September 2026 after the team lead's mutation testing found
  // that deleting it changed nothing, and chartDomain.test.ts now pins the band
  // itself rather than either of the two ways of producing it.
  const paddedLow = cutBelow || low < 0 ? low - pad : low;
  const paddedHigh = cutAbove || high > 0 ? high + pad : high;

  const domain: AxisDomain = [
    Math.floor(paddedLow / step) * step,
    Math.ceil(paddedHigh / step) * step,
  ];

  // Counted against the domain that is actually drawn, not against the fence
  // that suggested it. A value the fence called exceptional can still land
  // inside the padded, step-rounded domain, and a point drawn in its true
  // position must never be reported as pinned.
  const below = numbers.filter((v) => v < domain[0]).length;
  const above = numbers.filter((v) => v > domain[1]).length;

  return { domain, clamped: below + above > 0, below, above };
}

/** Where a value sits relative to the drawn domain. */
export function outsideDomain(value: number | null | undefined, domain: AxisDomain): "below" | "above" | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value < domain[0]) return "below";
  if (value > domain[1]) return "above";
  return null;
}

/**
 * The coordinate a value is drawn at: itself, or the edge it ran off.
 *
 * The real figure is never overwritten. The pinned coordinate is a separate
 * field on the plotted point, and every place that quotes a number to the
 * reader (the tooltip, the note under the chart, the table, both exports) reads
 * the real one.
 */
export function pinToDomain(value: number, domain: AxisDomain): number {
  if (value < domain[0]) return domain[0];
  if (value > domain[1]) return domain[1];
  return value;
}
